import { observer } from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import { Button } from "../../../../../components/inputs/button/button";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import onboardingNavigationStore from "../../../stores/onboarding-navigation-store";
import WalletConfigurationStore from "../../../stores/wallet-configuration-store";
import { WALLET_SCREEN_TYPE } from "../wallet-configuration-onboarding-screen";
import "./wallet-configuration-navigation-controls.scss";
import IconCheck from "../../../../../components/icons/icon-check/icon-check";

interface IWalletConfigurationavigationControlsType {
    store: WalletConfigurationStore;
};

const WalletConfigurationNavigationControls: React.FC<IWalletConfigurationavigationControlsType> = observer(({store}: IWalletConfigurationavigationControlsType) => {
    const scope = "dashboard-onboarding"; 
    const { getTranslation } = useDashboardTranslation();

    const getActionButtonText = (): string => {
        if (store.walletStore.isUpdating) {
            return getTranslation('VERIFYING_WALLET_DETAILS', scope);
        }
        if (store.paymentMethodVerified) {
            return getTranslation('VERIFIED_WALLET_DETAILS', scope);
        }
        return getTranslation('ACTIVATE_WALLET', scope);
    }

    const ActionButton: React.FC = observer(() => {
        switch (store.currentWalletScreen) {
            case WALLET_SCREEN_TYPE.ADDING_PAYMENT_METHOD:
                return <Button
                    text={getTranslation('SKIP_AND_ADD_IT_LATER', scope)}
                    variant={BUTTON_VARIANTS.Secondary}
                    disabled={store.isBusy}
                    onClick={store.nextStep}
                    hasLoader={true}
                    isLoading={store.isBusy}
                />;
            case WALLET_SCREEN_TYPE.PAYMENT_METHOD_ERROR:
                return <Button
                    text={getTranslation('SKIP', scope)}
                    variant={BUTTON_VARIANTS.Secondary}
                    onClick={store.backToAddingPaymentMethod}
                    hasLoader={true}
                />;
            case WALLET_SCREEN_TYPE.PAYMENT_METHOD_CONFIGURATION:
                const WhiteCheckIcon = () => <IconCheck color={'var(--color-neutral-1-white)'} />;

                /** We can not conditionally add leftIcon to button, so need to render whole button conditionally */
                if (store.paymentMethodVerified) {
                    return <Button
                        text={getActionButtonText()}
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        onClick={store.updateWalletAndNextStep}
                        hasLoader={true}
                        leftIcon={WhiteCheckIcon}
                        isLoading={store.walletStore.isUpdating}
                    />;
                } else {
                    return <Button
                        text={getActionButtonText()}
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        onClick={store.updateWalletAndNextStep}
                        hasLoader={true}
                        isLoading={store.isBusy}
                        disabled={store.validationErrors().length > 0}
                    />;
                }        
        }
    })

    return (
        <div className="wallet-configuration-navigation-controls">
            <div className="wallet-configuration-nav-buttons-back">
                <Button
                    text={getTranslation('BACK', scope)}
                    variant={BUTTON_VARIANTS.Transparent}
                    disabled={store.isBusy}
                    onClick={store.previousStep}
                />
            </div>
            <div className="wallet-configuration-nav-buttons-with-link">
                <ActionButton />
                <div className="wallet-configuration-nav-buttons-support-link" onClick={() => onboardingNavigationStore.openSupportChat()}>
                    <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('NEED_HELP', scope)}</FontBody1>
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.UNDER_LINE}>{getTranslation('TALK_TO_SUPPORT', scope)}</FontBody1>
                </div>
            </div>
        </div>
    );
});

export default WalletConfigurationNavigationControls;
